import React from "react";
import { Box, Container, Text, SimpleGrid, Flex } from "@chakra-ui/react";
import { motion } from "framer-motion";

const MotionBox = motion(Box);

const processSteps = [
  {
    number: "01",
    title: "Generated using strict academic frameworks",
    desc: "Every topic is built from standard reference texts and structured academic frameworks — no shortcuts, no guesswork.",
  },
  {
    number: "02",
    title: "Reviewed for clarity and accuracy",
    desc: "Each guide is rigorously checked for correctness and simplified to match how nursing students actually learn.",
  },
  {
    number: "03",
    title: "Refined by nurses and doctors",
    desc: "Professionals with real clinical experience review and refine every block for ward relevance and exam applicability.",
  },
];

export const About = () => {
  return (
    <Box
      bg="#293452"
      py={{ base: "64px", md: "96px" }}
      position="relative"
      overflow="hidden"
      className="dot-grid"
    >
      {/* Radial glow */}
      <Box
        position="absolute"
        top="50%"
        left="50%"
        transform="translate(-50%,-50%)"
        w="800px"
        h="800px"
        borderRadius="50%"
        background="radial-gradient(ellipse, rgba(0,149,200,0.07) 0%, transparent 70%)"
        pointerEvents="none"
      />

      <Container maxW="1200px" px={{ base: "5", md: "8" }} position="relative" zIndex={1}>

        <Flex direction="column" align="center" textAlign="center" mb={{ base: 12, md: 16 }}>
          <Text
            fontFamily="'Inter', sans-serif"
            fontSize="0.8125rem"
            fontWeight="600"
            letterSpacing="0.1em"
            textTransform="uppercase"
            color="#0095C8"
            mb={3}
          >
            Our Process
          </Text>
          <Text
            as="h2"
            fontFamily="'Public Sans', sans-serif"
            fontWeight="600"
            fontSize={{ base: "1.75rem", md: "2.375rem" }}
            color="white"
            lineHeight="1.25"
            mb={4}
          >
            Precision-built.{" "}
            <Box as="span" bgGradient="linear(135deg, #0095C8, #809CFF)" bgClip="text">
              Expert-refined.
            </Box>
          </Text>
          <Text
            fontFamily="'Inter', sans-serif"
            fontSize={{ base: "0.9375rem", md: "1.0625rem" }}
            color="rgba(255,255,255,0.6)"
            maxW="480px"
            lineHeight="1.7"
          >
            Each topic is created through a disciplined process.
          </Text>
        </Flex>

        {/* Steps — with connecting line on desktop */}
        <Box position="relative" mb={{ base: 10, md: 14 }}>
          {/* Connecting line (desktop only) */}
          <Box
            display={{ base: "none", md: "block" }}
            position="absolute"
            top="28px"
            left="calc(16.66% + 16px)"
            right="calc(16.66% + 16px)"
            h="2px"
            bgGradient="linear(to-r, #0095C8, #809CFF)"
            opacity="0.3"
            zIndex={0}
          />

          <SimpleGrid columns={{ base: 1, md: 3 }} spacing={8} position="relative" zIndex={1}>
            {processSteps.map((step, i) => (
              <MotionBox
                key={step.number}
                initial={{ opacity: 0, y: 28 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: i * 0.15 }}
              >
                <Box
                  p={7}
                  bg="#2F3859"
                  borderRadius="12px"
                  border="1px solid rgba(128,156,255,0.12)"
                  h="full"
                  position="relative"
                  overflow="hidden"
                  _hover={{
                    transform: "translateY(-4px)",
                    boxShadow: "0 8px 32px rgba(0,0,0,0.2)",
                    borderColor: "rgba(0,149,200,0.3)",
                  }}
                  transition="all 0.3s cubic-bezier(0.4,0,0.2,1)"
                >
                  {/* Top gradient accent */}
                  <Box
                    position="absolute"
                    top="0"
                    left="0"
                    right="0"
                    h="3px"
                    bgGradient="linear(to-r, #0095C8, #809CFF)"
                    borderRadius="12px 12px 0 0"
                  />

                  {/* Number circle */}
                  <Box
                    w="48px"
                    h="48px"
                    borderRadius="50%"
                    background="linear-gradient(135deg, #0095C8, #016293)"
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    mb={5}
                    boxShadow="0 4px 16px rgba(0,149,200,0.35)"
                  >
                    <Text
                      fontFamily="'Public Sans', sans-serif"
                      fontWeight="700"
                      fontSize="1rem"
                      color="white"
                    >
                      {step.number}
                    </Text>
                  </Box>

                  <Text
                    fontFamily="'Public Sans', sans-serif"
                    fontWeight="600"
                    fontSize="1.0625rem"
                    color="white"
                    mb={3}
                    lineHeight="1.4"
                  >
                    {step.title}
                  </Text>
                  <Text
                    fontFamily="'Inter', sans-serif"
                    fontSize="0.9rem"
                    color="rgba(255,255,255,0.6)"
                    lineHeight="1.7"
                  >
                    {step.desc}
                  </Text>
                </Box>
              </MotionBox>
            ))}
          </SimpleGrid>
        </Box>

        {/* Closing */}
        <Flex justify="center">
          <MotionBox
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
          >
            <Box
              textAlign="center"
              maxW="600px"
              px={6}
              py={5}
              bg="rgba(255,255,255,0.04)"
              borderRadius="10px"
              border="1px solid rgba(255,255,255,0.07)"
            >
              <Text
                fontFamily="'Inter', sans-serif"
                fontSize={{ base: "0.9375rem", md: "1.0625rem" }}
                color="rgba(255,255,255,0.75)"
                lineHeight="1.8"
              >
                Every block takes time to shape — so what you study is not just correct, but{" "}
                <Box as="span" color="#0095C8" fontWeight="700">usable.</Box>
              </Text>
            </Box>
          </MotionBox>
        </Flex>

      </Container>
    </Box>
  );
};
